import { useState, useEffect, useRef } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useWordMatch, type Difficulty, type GameMode } from './useWordMatch'
import { activities, themes } from '../../api/client'
import type { ThemeSummary } from '../../api/client'
import type { GameItem } from '../../i18n'
import { toGameItems, toGameItemsFromTheme } from '../utils'
import GameSetupScreen from '../GameSetupScreen'
import SaveErrorBanner from '../SaveErrorBanner'
import { ss } from '../gameSetupStyles'
import { useSessionMode } from '../../professional/SessionModeContext'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useActivitySession } from '../../hooks/useActivitySession'
import { s } from './wordMatchStyles'
import GameCompleteScreen from '../GameCompleteScreen'
import { formatGameTime } from '../utils'
import { PlayScreen } from './WordMatchPlayScreen'

interface WordMatchConfig {
  mode:       GameMode
  difficulty: Difficulty
  themeSlug:  string | null
  themeId:    number | null
}

interface WordMatchGameProps {
  onBack: () => void
  onGoToSubscription: () => void
}

type GameScreen = 'setup' | 'playing' | 'complete'

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

/* ── Root component ──────────────────────────────────────────── */
export default function WordMatchGame({ onBack, onGoToSubscription }: WordMatchGameProps) {
  const { t, language } = useTranslation()
  const sessionMode = useSessionMode()
  const { savedSession, saveConfig, clear } = useActivitySession<WordMatchConfig>('wordmatch', !sessionMode)
  const restored = savedSession?.config ?? null

  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : (restored ? 'playing' : 'setup'))
  const [difficulty, setDifficulty] = useState<Difficulty>((sessionMode?.config.difficulty as Difficulty) ?? restored?.difficulty ?? 'easy')
  const [mode,       setMode]       = useState<GameMode>((sessionMode?.config.mode as GameMode) ?? restored?.mode ?? 'images')

  const [classicItems,     setClassicItems]     = useState<GameItem[]>(t.items)
  const [gameItems,        setGameItems]         = useState<GameItem[]>(t.items)
  const [availableThemes,  setAvailableThemes]   = useState<ThemeSummary[]>([])
  const [selectedTheme,    setSelectedTheme]     = useState<{ slug: string; id: number } | null>(
    (sessionMode?.config.theme as { slug: string; id: number } | null | undefined) ??
    (restored?.themeSlug != null ? { slug: restored.themeSlug, id: restored.themeId! } : null)
  )
  const [themeLoading,     setThemeLoading]      = useState(() =>
    !!(sessionMode?.config.theme) || !!(restored?.themeSlug)
  )

  // Fetch classic items + available themes on mount / language change
  useEffect(() => {
    activities.get('wordmatch')
      .then((detail) => {
        const apiItems = detail.data?.items
        if (apiItems && apiItems.length > 0) {
          const items = toGameItems(apiItems, language)
          setClassicItems(items)
          if (!selectedTheme) setGameItems(items)
        }
      })
      .catch(() => { /* keep i18n fallback */ })

    themes.list('wordmatch')
      .then((list) => setAvailableThemes(list.filter((th) => th.imageCount >= 8)))
      .catch(() => {})
  }, [language]) // eslint-disable-line react-hooks/exhaustive-deps

  // When classic is selected, revert to classic items
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!selectedTheme) setGameItems(classicItems)
  }, [classicItems, selectedTheme])

  // When a theme is selected, load its images
  useEffect(() => {
    if (!selectedTheme) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setThemeLoading(true)
    themes.get(selectedTheme.slug)
      .then((theme) => setGameItems(toGameItemsFromTheme(theme.images, language)))
      .catch(() => setSelectedTheme(null))
      .finally(() => setThemeLoading(false))
  }, [selectedTheme, language])

  const { state, resetGame, selectOption, setInputValue, submitInput, toggleHint } =
    useWordMatch(difficulty, gameItems, mode)

  const { gameComplete, moves, startTime, endTime } = state

  const { sessionId } = useGameSession({
    activitySlug: 'wordmatch',
    difficulty,
    mode,
    enabled: screen === 'playing',
  })

  // In session mode: reinitialize game state once the configured theme finishes loading.
  // The hook initializes with classic items on mount (theme not yet fetched); this corrects it.
  const sessionThemeResetDone = useRef(false)
  useEffect(() => {
    if (!sessionMode?.config.theme && !restored?.themeSlug) return
    if (sessionThemeResetDone.current) return
    if (themeLoading) return
    sessionThemeResetDone.current = true
    resetGame()
  }, [themeLoading, sessionMode, resetGame]) // eslint-disable-line react-hooks/exhaustive-deps

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete,
    activitySlug:  'wordmatch',
    difficulty,
    mode,
    moves,
    startTime,
    endTime,
    pairsTotal:   state.questions.length,
    pairsMatched: state.answered,
    themeId:      selectedTheme?.id,
    sessionId,
    onCompleted:  () => { clear(); setScreen('complete') },
  })

  const handleStart = () => {
    resetGame()
    saveConfig({ mode, difficulty, themeSlug: selectedTheme?.slug ?? null, themeId: selectedTheme?.id ?? null })
    setScreen('playing')
  }

  if ((sessionMode || restored?.themeSlug) && themeLoading) {
    return <div style={s.loadRoot}>…</div>
  }

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}
      {screen === 'setup' && (
        <SetupScreen
          difficulty={difficulty}
          mode={mode}
          availableThemes={availableThemes}
          selectedTheme={selectedTheme}
          themeLoading={themeLoading}
          onDifficultyChange={setDifficulty}
          onModeChange={setMode}
          onThemeChange={(th) => setSelectedTheme(th ? { slug: th.slug, id: th.id } : null)}
          onStart={handleStart}
          onBack={onBack}
          onGoToSubscription={onGoToSubscription}
        />
      )}
      {screen === 'playing' && (
        <PlayScreen
          state={state}
          difficulty={difficulty}
          mode={mode}
          onSelectOption={selectOption}
          onSetInputValue={setInputValue}
          onSubmitInput={submitInput}
          onToggleHint={toggleHint}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
        />
      )}
      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🎉"
          title={t.wordmatch.complete.title}
          subtitle={t.wordmatch.complete.subtitle}
          stats={[
            { value: String(state.moves), label: t.wordmatch.complete.movesLabel },
            { value: formatGameTime(state.startTime, state.endTime), label: t.wordmatch.complete.timeLabel },
            { value: t.wordmatch.difficulty[difficulty].label, label: t.wordmatch.complete.levelLabel },
          ]}
          playAgainLabel={t.wordmatch.complete.playAgain}
          changeLevelLabel={t.wordmatch.complete.changeLevel}
          backLabel={t.wordmatch.complete.backToDashboard}
          onPlayAgain={() => {
            resetGame()
            saveConfig({ mode, difficulty, themeSlug: selectedTheme?.slug ?? null, themeId: selectedTheme?.id ?? null })
            setScreen('playing')
          }}
          onChangeLevel={() => { clear(); setScreen('setup') }}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ────────────────────────────────────────────── */
function SetupScreen({ difficulty, mode, availableThemes, selectedTheme, themeLoading, onDifficultyChange, onModeChange, onThemeChange, onStart, onBack, onGoToSubscription }: {
  difficulty:         Difficulty
  mode:               GameMode
  availableThemes:    ThemeSummary[]
  selectedTheme:      { slug: string; id: number } | null
  themeLoading:       boolean
  onDifficultyChange: (d: Difficulty) => void
  onModeChange:       (m: GameMode) => void
  onThemeChange:      (t: ThemeSummary | null) => void
  onStart:            () => void
  onBack:             () => void
  onGoToSubscription: () => void
}) {
  const { t } = useTranslation()
  const wm = t.wordmatch

  const MODES: GameMode[] = ['images', 'definitions', 'anagram']

  const preview = mode === 'images' ? (
    <div style={s.previewCard}>
      <div style={s.previewDefLabel}>{wm.mode.images.label}</div>
      <div style={s.previewImageBox}>🌸</div>
      <div style={s.previewOptionsRow}>
        {['Mariposa', 'Flor de cerezo', 'Montaña', 'Luna'].map((w, i) => (
          <div key={w} style={{ ...s.previewOption, ...(i === 1 ? s.previewOptionCorrect : {}) }}>
            {w}
          </div>
        ))}
      </div>
    </div>
  ) : mode === 'definitions' ? (
    <div style={s.previewCard}>
      <div style={s.previewDefLabel}>{wm.mode.definitions.label}</div>
      <div style={s.previewDefBox}>Hermosa flor que florece en primavera</div>
      <div style={s.previewOptionsRow}>
        {['Mariposa', 'Flor de cerezo', 'Montaña', 'Luna'].map((w, i) => (
          <div key={w} style={{ ...s.previewOption, ...(i === 1 ? s.previewOptionCorrect : {}) }}>
            {w}
          </div>
        ))}
      </div>
    </div>
  ) : (
    <div style={s.previewCard}>
      <div style={s.previewDefLabel}>{wm.mode.anagram.label}</div>
      <div style={s.previewScramble}>R E O C R A Z E</div>
      <div style={s.previewOptionsRow}>
        {['Mariposa', 'Flor de cerezo', 'Montaña', 'Luna'].map((w, i) => (
          <div key={w} style={{ ...s.previewOption, ...(i === 1 ? s.previewOptionCorrect : {}) }}>
            {w}
          </div>
        ))}
      </div>
    </div>
  )

  const modeSelector = (
    <section className="fade-up" style={{ animationDelay: '0.1s' }}>
      <h2 style={ss.optionLabel}>{wm.chooseModeTitle}</h2>
      <div style={ss.modeGrid}>
        {MODES.map((m) => (
          <button
            key={m}
            onClick={() => onModeChange(m)}
            style={{ ...ss.modeCard, ...(mode === m ? ss.modeCardActive : {}) }}
            aria-pressed={mode === m}
          >
            <span style={s.modeEmoji}>{m === 'images' ? '🖼️' : m === 'definitions' ? '📖' : '🔤'}</span>
            <span style={s.modeLabel}>{wm.mode[m].label}</span>
            <span style={s.modeDesc}>{wm.mode[m].description}</span>
          </button>
        ))}
      </div>
    </section>
  )

  return (
    <GameSetupScreen
      title={wm.title}
      subtitle={wm.subtitle}
      backLabel={wm.backToDashboard}
      onBack={onBack}
      availableThemes={availableThemes}
      selectedTheme={selectedTheme}
      onThemeChange={onThemeChange}
      classicEmoji="📖"
      classicLabel={wm.classicTheme}
      chooseThemeLabel={wm.chooseThemeTitle}
      onGoToSubscription={onGoToSubscription}
      difficulties={DIFFICULTIES.map((id) => ({ id, ...wm.difficulty[id] }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={(id) => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={wm.chooseDifficultyTitle}
      startLabel={wm.startBtn}
      onStart={onStart}
      themeLoading={themeLoading}
      preview={preview}
    >
      {modeSelector}
    </GameSetupScreen>
  )
}

